import type { ElementType, ReactNode } from "react";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Line, LineChart, ResponsiveContainer } from "recharts";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { DeltaBadge } from "@/features/dashboard/components/DeltaBadge";
import {
  CHART_NEGATIVE,
  CHART_POSITIVE,
  CHART_PRIMARY,
  CHART_WARNING,
} from "@/features/reports/components/chartTheme";
import { cn } from "@/lib/cn";

/**
 * Accent palette for the rich `plain` layout (TASK-3880). Each accent maps to a
 * tinted icon-chip (`bg-{accent}-50` + `text-{accent}-600`) and a matching
 * sparkline stroke pulled from `chartTheme`. Tailwind classes are written in
 * full (never interpolated) so the JIT compiler keeps them.
 */
export type KpiAccent =
  | "blue"
  | "emerald"
  | "amber"
  | "rose"
  | "violet"
  | "slate";

/** Icon-chip tint classes per accent — `bg-{accent}-50` + `text-{accent}-600`. */
const ACCENT_CHIP: Record<KpiAccent, string> = {
  blue: "bg-blue-50 text-blue-600",
  emerald: "bg-emerald-50 text-emerald-600",
  amber: "bg-amber-50 text-amber-600",
  rose: "bg-rose-50 text-rose-600",
  violet: "bg-violet-50 text-violet-600",
  slate: "bg-slate-100 text-slate-600",
};

/** Sparkline stroke color per accent — reuses the shared chart palette. */
const ACCENT_SPARK: Record<KpiAccent, string> = {
  blue: CHART_PRIMARY,
  emerald: CHART_POSITIVE,
  amber: CHART_WARNING,
  rose: CHART_NEGATIVE,
  violet: "#7c3aed", // violet-600 (no chartTheme token)
  slate: "#475569", // slate-600 (no chartTheme token)
};

/**
 * Structured delta for the rich `plain` layout — mirrors the {@link DeltaBadge}
 * props so the card can render the badge itself instead of each caller wiring
 * it via the `badge` slot. A bare `number` is also accepted (legacy gradient
 * `delta` + a convenience for plain cards) and is treated as `{ changePct }`.
 */
export interface KpiDelta {
  /** Signed percentage change; `null` → "New" or em-dash per DeltaBadge rules. */
  changePct: number | null;
  /** Current activity with zero prior activity → "New" label. */
  isNew?: boolean;
  /** Magnitude exceeds 999% → abbreviated ">+999%" / "<-999%". */
  capped?: boolean;
}

export interface KpiCardProps {
  /**
   * Visual variant:
   * - `"plain"` (default) — flat white bordered enterprise card. The single
   *   standardized style for ALL admin/dealer KPI & metric strips (dashboard,
   *   billing, tenant detail, dealer detail, dealer revenue) — TASK-3690.
   * - `"gradient"` — legacy glassmorphism gradient card, kept only for the
   *   dispatcher dashboard and reports KPI strip (out of the admin scope).
   */
  variant?: "gradient" | "plain";
  /** Icon component. Rendered by both variants (plain shows it muted, small). */
  icon?: ElementType;
  /** Tailwind gradient from/to classes, e.g. "from-blue-500 to-indigo-600" (gradient variant). */
  gradient?: string;
  /**
   * Accent color for the rich `plain` layout (TASK-3880). When set together with
   * `icon`, the icon renders inside a tinted chip (`bg-{accent}-50` /
   * `text-{accent}-600`) and any `sparkline` adopts the matching stroke color.
   * Ignored by the gradient variant. When omitted, the plain card keeps its
   * original muted-icon look (fully backward compatible).
   */
  accent?: KpiAccent;
  /**
   * Optional sparkline series (plain layout). When 2+ finite numbers are
   * provided, a ~40px-tall Recharts line renders below the value with NO axes,
   * grid, or legend. The stroke color comes from `accent` (defaults to the
   * brand primary). Absent/empty → nothing renders (graceful degradation).
   */
  sparkline?: number[];
  /**
   * Flip the delta's positive/negative coloring for down-is-good metrics
   * (churn, cost, overdue). Forwarded to {@link DeltaBadge}'s `invert`. Only
   * affects the structured `delta` rendering in the plain layout.
   */
  deltaInvert?: boolean;
  label: string;
  /**
   * The KPI value to display. `undefined` → loading Skeleton; `null` or the
   * literal em-dash `"—"` → a muted "no data for period" treatment (plain
   * variant); anything else is rendered as the value.
   */
  value: number | string | null | undefined;
  /**
   * Value text size (plain variant only) — TASK-3915. Controls how big the
   * value renders inside the unchanged card chrome:
   * - `"lg"` (default) — the original `text-3xl` numeric size. Existing usages
   *   are unaffected.
   * - `"sm"` — a smaller, text-friendly `text-lg` size for non-numeric facts
   *   (dates like "28 июн. 2026 г.", version strings like "dev") that look
   *   oversized and wrap awkwardly in the numeric slot.
   * Ignored by the gradient variant.
   */
  valueSize?: "lg" | "sm";
  /** Optional sub-label shown below the label in a lighter color (gradient variant). */
  subLabel?: string;
  /** When true, renders a larger "hero" variant with bigger value text (gradient variant). */
  heroSize?: boolean;
  /** When true, renders the value in muted white (gradient variant). */
  valueMuted?: boolean;
  /**
   * Delta indicator. Two accepted shapes:
   * - **gradient variant** — a bare `number`: positive = today higher, negative
   *   = today lower, 0 = equal; rendered as a `↑N / ↓N` text line.
   * - **plain layout (TASK-3880)** — a {@link KpiDelta} object (or a bare
   *   `number`, coerced to `{ changePct }`): rendered via the shared
   *   {@link DeltaBadge} next to the value, honoring `deltaInvert`.
   *
   * When undefined, no delta is rendered. The `badge` slot still works for
   * callers that prefer to pass a fully-built `DeltaBadge` themselves.
   */
  delta?: number | KpiDelta;
  /**
   * Optional badge node rendered next to the value (plain variant) or absolutely
   * positioned in the top-right corner (gradient variant — used for the trend
   * DeltaBadge). When undefined nothing is rendered.
   */
  badge?: ReactNode;
  /** Red highlight for the value + border (plain variant). */
  highlight?: boolean;
  /**
   * Semantic color for the value text (plain variant). Lets a card express the
   * meaning of its figure — `"positive"` → emerald, `"negative"` → rose,
   * `"neutral"` → default gray. Ignored when `highlight` is set (highlight wins).
   * Used by the Plan-vs-Actual profit/loss & trend cards (TASK-3839).
   */
  valueTone?: "negative" | "neutral" | "positive";
  /**
   * Optional explanatory hint. When set (plain variant), a small info icon is
   * rendered next to the label; hovering/focusing it shows this text in a
   * Tooltip, and the text is also the button's accessible name so a screen
   * reader announces it. The card supplies its own scoped `TooltipProvider`,
   * so no provider ancestor is required.
   */
  hint?: string;
  /**
   * Optional navigation target (plain variant). When set, the card's inner
   * content is wrapped in a react-router `<Link>` so the whole card is a single
   * clickable target with a hover/focus affordance. When omitted the card stays
   * a non-interactive `<div>` (unchanged). Ignored by the gradient variant.
   *
   * Nested-link guard: when both `to` and `hint` are set the hint info-icon
   * renders as a non-interactive `<span>` (with `title`/`aria-label`) rather
   * than a `<button>`, so there is never an interactive element nested inside
   * the card `<Link>` (invalid HTML).
   */
  to?: string;
  className?: string;
  // Deprecated props kept for backward compatibility — ignored in render
  /** @deprecated use gradient instead */
  iconColor?: string;
  /** @deprecated use gradient instead */
  cardBg?: string;
  /** @deprecated use gradient instead */
  iconBg?: string;
  /** @deprecated use gradient instead */
  accentColor?: string;
  /** @deprecated use gradient instead */
  accentBorder?: string;
}

/**
 * Shared KPI card. The flat `"plain"` enterprise style is the default and the
 * single source of truth for every admin/dealer KPI strip (TASK-3690). The
 * legacy `"gradient"` glassmorphism variant is retained only for the dispatcher
 * dashboard and reports KPI strip, which opt in via `variant="gradient"`.
 */
export function KpiCard(props: KpiCardProps) {
  if (props.variant === "gradient") {
    return <GradientKpiCard {...props} />;
  }
  return <PlainKpiCard {...props} />;
}

/** The em-dash a null KPI figure resolves to (shared with the report tabs). */
const NULL_DASH = "—";

const TONE_CLASS: Record<NonNullable<KpiCardProps["valueTone"]>, string> = {
  positive: "text-emerald-600",
  negative: "text-rose-600",
  neutral: "text-gray-900",
};

/** Normalize the `delta` prop to a {@link KpiDelta} (a bare number → changePct). */
function toKpiDelta(
  delta: number | KpiDelta | undefined,
): KpiDelta | undefined {
  if (delta === undefined) {
    return undefined;
  }
  return typeof delta === "number" ? { changePct: delta } : delta;
}

/** Value text size per `valueSize` (plain variant) — TASK-3915. */
const VALUE_SIZE_CLASS: Record<
  NonNullable<KpiCardProps["valueSize"]>,
  string
> = {
  lg: "text-3xl",
  sm: "text-lg",
};

function PlainKpiCard({
  icon: Icon,
  label,
  value,
  valueSize = "lg",
  badge,
  highlight,
  valueTone,
  hint,
  accent,
  sparkline,
  delta,
  deltaInvert,
  to,
  className,
}: KpiCardProps) {
  const { t } = useTranslation();
  // A bare number delta is coerced to `{ changePct }`; an object passes through.
  const kpiDelta = toKpiDelta(delta);
  // The icon-chip tint is only applied when an accent is set; otherwise the
  // original muted-icon look is preserved (backward compatible).
  const chipClass = accent ? ACCENT_CHIP[accent] : undefined;
  // With no accent the sparkline uses the fixed brand-blue default; an accent
  // keeps its semantic color. (The admin/dealer panel is intentionally not
  // brand-themed, so the sparkline default stays the fixed `CHART_PRIMARY`.)
  const sparkColor = accent ? ACCENT_SPARK[accent] : CHART_PRIMARY;
  // Sparkline renders only with real data (>= 2 finite points). Anything else
  // degrades gracefully to nothing.
  const sparkData =
    sparkline && sparkline.filter((n) => Number.isFinite(n)).length >= 2
      ? sparkline.map((y, x) => ({ x, y }))
      : null;
  // Loading (value not resolved yet) is visually distinct from a settled
  // null-result. A null result resolves to the shared em-dash string.
  const isLoading = value === undefined;
  const isNullResult = !isLoading && (value === null || value === NULL_DASH);

  /**
   * The ⓘ affordance. Defined here and rendered INSIDE the label <p> so it
   * flows inline with the text instead of taking its own flex track — see the
   * note at the render site.
   */
  const hintNode = !hint ? null : to ? (
    // Nested-link guard: inside a card <Link> the hint must NOT be an
    // interactive <button> (invalid nested-interactive HTML). Render it as a
    // non-interactive <span> carrying the text via `title` / `aria-label`.
    <span
      title={hint}
      aria-label={hint}
      className="mt-px shrink-0 text-gray-400"
    >
      <Info className="h-3.5 w-3.5" aria-hidden="true" />
    </span>
  ) : (
    /* Own provider, scoped to this card. Radix THROWS ("`Tooltip` must be used
       within `TooltipProvider`") rather than degrading, so a KpiCard rendered
       outside the app shell — any unit test, Storybook, or a future surface —
       would crash on a hint. Nesting inside the app-level provider (App.tsx,
       BUG-521) is explicitly supported, so this is safe and makes the card
       self-sufficient.

       The trigger is a real <button>, so it is in the tab order and Radix opens
       the tooltip on FOCUS as well as hover. `aria-label` carries the full text
       into the accessible name, so a screen reader announces the explanation
       even though the visual tooltip is a hover/focus affordance.

       Touch: Radix opens a tooltip on tap for a focusable trigger, but that
       alone is not dependable across mobile browsers, so the native `title` is
       kept as a second channel, and the negative-margin padding box gives the
       14px glyph a ~44px hit area without adding any layout size. */
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={hint}
            title={hint}
            className="-m-2 mt-px inline-flex shrink-0 items-center justify-center p-2 text-gray-400 transition-colors hover:text-gray-600 focus:outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-1"
          >
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{hint}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const inner = (
    <>
      {/* `items-start` + a reserved two-line min-height when a hint is present:
          the ⓘ adds a ~14px track, and three RU labels («Маршрутов сегодня»,
          «Маршрутов в архиве», «Загрузка автопарка») have no spare room for it
          at 1280, so they wrap to a second line. Reserving that line up front
          means the wrap consumes space the row already owns — every card in a
          row keeps an identical height and nothing below shifts. */}
      <div
        className={cn(
          "mb-1 flex gap-1.5",
          hint ? "min-h-[2rem] items-start" : "items-center",
        )}
      >
        {/* With an accent, the icon becomes a tinted chip; without one it keeps
            its original muted-inline look (backward compatible). */}
        {Icon && chipClass && (
          <span
            className={cn(
              "mr-0.5 inline-flex h-6 w-6 items-center justify-center rounded-lg",
              chipClass,
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden="true" strokeWidth={2} />
          </span>
        )}
        {Icon && !chipClass && (
          <Icon
            className="h-3.5 w-3.5 text-gray-400"
            aria-hidden="true"
            strokeWidth={2}
          />
        )}
        {/* WCAG AA (TASK-3912): gray-500 (~4.6:1 on white) replaces gray-400
            (~2.85:1) so the uppercase micro-label clears the 4.5:1 threshold. */}
        <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">
          {label}
        </p>
        {hintNode}
        {/* Nested-link guard: inside a card `<Link>` the hint must NOT be an
            interactive `<button>` (invalid nested-interactive HTML). Render it
            as a non-interactive `<span>` carrying the text via `title` /
            `aria-label`; outside a link it stays the full Tooltip button. */}
      </div>
      {isLoading ? (
        <Skeleton className="mt-1 h-8 w-24" />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <p
              className={cn(
                "font-bold leading-tight tracking-tight",
                VALUE_SIZE_CLASS[valueSize],
                isNullResult
                  ? "text-gray-300"
                  : highlight
                    ? "text-red-600"
                    : valueTone
                      ? TONE_CLASS[valueTone]
                      : "text-gray-900",
              )}
            >
              {value === null ? NULL_DASH : value}
            </p>
            {badge}
            {/* Structured delta → shared DeltaBadge, with down-is-good inversion. */}
            {kpiDelta && (
              <DeltaBadge
                changePct={kpiDelta.changePct}
                isNew={kpiDelta.isNew}
                capped={kpiDelta.capped}
                invert={deltaInvert}
              />
            )}
          </div>
          {isNullResult && (
            <p className="mt-0.5 text-[11px] font-medium text-gray-300">
              {t("common.noDataForPeriod")}
            </p>
          )}
          {/* ~40px sparkline — no axes/grid/legend; renders only with real data. */}
          {sparkData && (
            <div className="mt-3 h-10 w-full" data-testid="kpi-sparkline">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={sparkData}
                  margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
                >
                  <Line
                    type="monotone"
                    dataKey="y"
                    stroke={sparkColor}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </>
  );

  const cardClass = cn(
    "block w-full rounded-2xl border border-gray-100 bg-white p-5 shadow-sm",
    highlight && "border-red-200",
    className,
  );

  // With a `to`, the whole card is a single navigation target: a Link with a
  // hover lift + focus ring. Without one it stays the original inert div.
  if (to) {
    return (
      <Link
        to={to}
        className={cn(
          cardClass,
          "transition-colors hover:border-gray-200 hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2",
        )}
      >
        {inner}
      </Link>
    );
  }

  return <div className={cardClass}>{inner}</div>;
}

function GradientKpiCard({
  icon: Icon,
  gradient = "from-gray-500 to-gray-600",
  label,
  value,
  subLabel,
  heroSize = false,
  valueMuted = false,
  delta,
  badge,
  className,
}: KpiCardProps) {
  const { t } = useTranslation();
  const isLoading = value === undefined;
  // The gradient variant only understands the legacy numeric delta (rendered as
  // a "↑N / ↓N" text line). A structured KpiDelta is plain-layout only and is
  // ignored here so the gradient stays visually unchanged.
  const numericDelta = typeof delta === "number" ? delta : undefined;

  return (
    <div
      className={cn(
        `relative overflow-hidden rounded-2xl border border-white/20 bg-gradient-to-br shadow-lg transition-all duration-200 hover:-translate-y-1 hover:shadow-xl`,
        gradient,
        heroSize ? "p-6" : "p-5",
        className,
      )}
    >
      {/* Glass overlay */}
      <div className="pointer-events-none absolute inset-0 bg-white/5" />

      {/* Optional corner badge slot (e.g. trend DeltaBadge). Only shown once the
          value has settled, matching the previous wrapper-overlay behavior. */}
      {badge !== undefined && !isLoading && (
        <div className="absolute right-4 top-5 z-10">{badge}</div>
      )}

      <div className="relative z-10">
        {/* Icon badge */}
        {Icon && (
          <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
            <Icon className="h-5 w-5 text-white" aria-hidden="true" />
          </div>
        )}

        {isLoading ? (
          <>
            <Skeleton
              className={cn(
                "mb-1.5 w-16 bg-white/30",
                heroSize ? "h-10" : "h-8",
              )}
            />
            <Skeleton className="h-3 w-24 bg-white/30" />
          </>
        ) : (
          <>
            <p
              className={cn(
                "font-bold leading-none tracking-tight",
                heroSize ? "text-4xl" : "text-3xl",
                valueMuted ? "text-white/50" : "text-white",
              )}
            >
              {value === null ? NULL_DASH : value}
            </p>
            <p className="mt-2 text-xs font-semibold uppercase tracking-widest text-white/70">
              {label}
            </p>
            {subLabel && (
              <p className="mt-0.5 text-xs text-white/50">{subLabel}</p>
            )}
            {numericDelta !== undefined && (
              <p className="mt-1 text-xs text-white/70">
                {numericDelta > 0
                  ? `↑${numericDelta}`
                  : numericDelta < 0
                    ? `↓${Math.abs(numericDelta)}`
                    : "="}{" "}
                {t("dashboard.kpi.vsYesterday")}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
